<template>
    <form ref="personalForm" class="grid grid-cols-12 gap-3 lg:p-0" @submit.prevent="onSubmitPersonalForm()">
        <div v-if="businessAccountAllowed" class="form-control col-span-12">
            <div class="label">
                <span class="label-text">{{ $t('customer.editInfo.accountTypeLabel') }}</span>
            </div>
            <div class="flex gap-6">
                <label for="private-type" class="flex items-center cursor-pointer">
                    <input
                        id="private-type"
                        v-model="personalFormData.accountType"
                        type="radio"
                        name="accountType"
                        value="private"
                        class="radio radio-primary mr-2"
                    >
                    <span class="label-text">{{ $t('customer.editInfo.accountTypePrivate') }}</span>
                </label>
                <label for="business-type" class="flex items-center cursor-pointer">
                    <input
                        id="business-type"
                        v-model="personalFormData.accountType"
                        type="radio"
                        name="accountType"
                        value="business"
                        class="radio radio-primary mr-2"
                    >
                    <span class="label-text">{{ $t('customer.editInfo.accountTypeBusiness') }}</span>
                </label>
            </div>
        </div>

        <div class="form-control col-span-12 lg:col-span-6">
            <label for="salutation" class="label">
                <span class="label-text">{{ $t('customer.editInfo.labelSalutation') }}</span>
            </label>

            <div class="flex flex-wrap gap-6">
                <select
                    id="salutation"
                    v-model="personalFormData.salutationId"
                    required
                    class="select select-bordered w-full text-base"
                >
                    <option v-for="salutation in salutations" :key="salutation.id" :value="salutation.id">
                        {{ salutation?.translated?.displayName }}
                    </option>
                </select>
            </div>
        </div>

        <div class="col-span-12 grid grid-cols-12 gap-3 lg:gap-6">
            <div class="form-control col-span-12 lg:col-span-6">
                <label for="firstName" class="label">
                    <span class="label-text">{{ $t('customer.editInfo.labelFirstname') }}</span>
                </label>
                <input
                    id="firstName"
                    v-model="personalFormData.firstName"
                    required
                    type="text"
                    :placeholder="$t('customer.editInfo.placeholderFirstname')"
                    class="input input-bordered w-full"
                >
            </div>

            <div class="form-control col-span-12 lg:col-span-6">
                <label for="lastName" class="label">
                    <span class="label-text">{{ $t('customer.editInfo.labelLastname') }}</span>
                </label>
                <input
                    id="lastName"
                    v-model="personalFormData.lastName"
                    required
                    type="text"
                    :placeholder="$t('customer.editInfo.placeholderLastname')"
                    class="input input-bordered w-full"
                >
            </div>
        </div>

        <div v-if="personalFormData.accountType === 'business'" class="form-control col-span-12">
            <label for="company" class="label">
                <span class="label-text">{{ $t('customer.editInfo.companyLabel') }}</span>
            </label>
            <input
                id="company"
                v-model="personalFormData.company"
                required
                type="text"
                :placeholder="$t('customer.editInfo.companyPlaceholder')"
                class="input input-bordered w-full"
            >
        </div>

        <div v-if="personalFormData.accountType === 'business'" class="form-control col-span-12">
            <label for="vat-id" class="label">
                <span class="label-text">{{ $t('customer.editInfo.vatIdLabel') }}</span>
            </label>
            <input
                id="vat-id"
                ref="vatIdInput"
                v-model="personalFormData.vatId"
                required
                type="text"
                :placeholder="$t('customer.editInfo.vatIdPlaceholder')"
                class="input input-bordered w-full"
                @input="validateVatId"
            >
        </div>

        <div class="form-control col-span-12">
            <label for="register-dob" class="label">
                <span class="label-text">
                    {{ $t('customer.editInfo.dobLabel') }}
                    <span class="text-base-content-subtle font-normal">({{ $t('customer.editInfo.optional') }})</span>
                </span>
            </label>
            <div class="join w-full">
                <input
                    id="register-dob"
                    ref="dobInput"
                    v-model="personalFormData.dateOfBirth"
                    type="date"
                    class="input input-bordered w-full border-r-0 join-item"
                >
                <div
                    role="button"
                    tabindex="0"
                    class="btn btn-square btn-outline border-border border-l-0 join-item"
                    @click="togglePicker()"
                    @keydown.enter.prevent="togglePicker()"
                >
                    <MiscIcon name="calendar" class="w-5 h-5" />
                </div>
            </div>
            <div class="mt-1 text-sm text-base-content-subtle">
                {{ $t('customer.editInfo.dobInfo') }}
            </div>
        </div>

        <div class="col-span-12 mt-6">
            <button
                type="submit"
                class="btn btn-sm btn-secondary"
                :disabled="loadingPersonalForm"
                :aria-disabled="loadingPersonalForm"
                data-cy="customer-edit-info-submit"
            >
                <span v-if="loadingPersonalForm" class="loading" />
                {{ $t('customer.editInfo.submit') }}
            </button>
        </div>
    </form>
</template>

<script setup lang="ts">
import { validateVatId as validateVatIdHelper } from '@/utils/vat-id-validation'
import { ApiClientError } from '@shopware/api-client'

const { t } = useI18n()
const { user, updatePersonalInfo } = useUser()
const { getSalutations } = useSalutations()
const { businessAccountAllowed } = useBusinessAccount()

const salutations = computed(() => getSalutations.value)
const dobInput = ref()
const vatIdInput = ref()

const personalFormData: {
    salutationId: string,
    firstName: string,
    lastName: string,
    dateOfBirth: null | string,
    accountType: string,
    company: string,
    vatId: string
} = reactive({
    salutationId: '',
    firstName: '',
    lastName: '',
    dateOfBirth: null,
    accountType: 'private',
    company: '',
    vatId: ''
})

function fillPersonalForm () {
    personalFormData.salutationId = user?.value?.salutationId != null ? user?.value?.salutationId : ''
    personalFormData.firstName = user?.value?.firstName != null ? user?.value?.firstName : ''
    personalFormData.lastName = user?.value?.lastName != null ? user?.value?.lastName : ''
    personalFormData.accountType = user?.value?.accountType != null ? user?.value?.accountType : 'private'
    personalFormData.company = user?.value?.company != null ? user?.value?.company : ''
    personalFormData.vatId = user?.value?.vatIds != null && user?.value?.vatIds.length > 0 && user?.value?.vatIds[0] ? user?.value?.vatIds[0] : ''

    if (user?.value?.birthday != null) {
        const customerDoB = new Date(user.value.birthday)
        personalFormData.dateOfBirth = `${customerDoB.getFullYear()}-${customerDoB.getMonth() <= 9 ? '0' : ''}${customerDoB.getMonth() + 1}-${customerDoB.getDate() <= 9 ? '0' : ''}${customerDoB.getDate()}`
    }
}

fillPersonalForm()

const personalForm = ref()
const loadingPersonalForm = ref(false)
const { validateForm } = formValidation()
const { showNotification } = useNotification()
async function onSubmitPersonalForm () {
    loadingPersonalForm.value = true

    const isValid = await validateForm(personalForm.value)
    if (!isValid) {
        loadingPersonalForm.value = false
        return
    }

    try {
        const dob = personalFormData.dateOfBirth ? new Date(personalFormData.dateOfBirth) : null
        const base = {
            salutationId: personalFormData.salutationId,
            firstName: personalFormData.firstName,
            lastName: personalFormData.lastName,
            ...(dob != null && {
                birthdayDay: dob.getDate(),
                birthdayMonth: dob.getMonth() + 1,
                birthdayYear: dob.getFullYear(),
            }),
        }

        if (personalFormData.accountType === 'business') {
            await updatePersonalInfo({ ...base, accountType: 'business', company: personalFormData.company, vatIds: [personalFormData.vatId] })
        } else {
            await updatePersonalInfo({ ...base, accountType: 'private' })
        }
        loadingPersonalForm.value = false
        showNotification(t('customer.editInfo.success'), 'success')
    } catch (e: unknown) {
        loadingPersonalForm.value = false

        if (e instanceof ApiClientError) {
            const firstError = e.details?.errors?.[0]
            if (firstError?.code === 'VIOLATION::CUSTOMER_VAT_ID_INVALID') {
                showNotification(t('customer.editInfo.vatIdInvalid'), 'error')
            } else {
                showNotification(firstError?.detail ?? t('customer.editInfo.error'), 'error')
            }
        } else {
            showNotification(t('customer.editInfo.error'), 'error')
        }
    }
}

function togglePicker() {
    dobInput.value.showPicker()
}

function validateVatId () {
    validateVatIdHelper(vatIdInput.value, personalFormData.vatId, t('customer.editInfo.vatIdInvalid'))
}
</script>

<style lang="postcss">
#register-dob[type="date"]::-webkit-inner-spin-button,
#register-dob[type="date"]::-webkit-calendar-picker-indicator {
    display: none;
    -webkit-appearance: none;
}
</style>
